import { useMemo } from 'react';
import { SMS, TaskStatus } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { MessagesTypes } from '@frontend/api-messaging';
import { useTranslation } from '@frontend/i18n';
import { useTaskCitation } from '@frontend/task-center-scope';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { Chat, Chip, useAlert } from '@frontend/design-system';

const getSortedTasksByStatus = (tasks: SMS['tasks'], status: TaskStatus) =>
  tasks!.filter((task) => task.status === status).sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));

const getLatestSMSTask = (tasks: MessagesTypes.InboxListItem['tasks']) => {
  if (!tasks || tasks.length === 0) {
    return undefined;
  }

  const sortedTasks = [...tasks].sort((a, b) => {
    const dateA = a.smsCreatedAt || '';
    const dateB = b.smsCreatedAt || '';
    return dateB.localeCompare(dateA);
  });

  return sortedTasks[0];
};

const AI_UUID = '60e5e959-cd7a-4495-a58c-9e0cbd0f0ec8';

const getTasksData = (tasks: SMS['tasks']) => {
  if (!tasks || tasks.length === 0) {
    return undefined;
  }

  const toDoTasks = getSortedTasksByStatus(tasks, TaskStatus.TASK_STATUS_TO_DO);
  const inProgressTasks = getSortedTasksByStatus(tasks, TaskStatus.TASK_STATUS_IN_PROGRESS);
  const doneTasks = getSortedTasksByStatus(tasks, TaskStatus.TASK_STATUS_DONE);

  return {
    latestToDoTaskId: toDoTasks[0]?.taskId,
    latestInProgressTaskId: inProgressTasks[0]?.taskId,
    latestDoneTaskId: doneTasks[0]?.taskId,
    toDoCount: toDoTasks.length,
    inProgressCount: inProgressTasks.length,
    doneCount: doneTasks.length,
  };
};

type SMSTaskProps = {
  tasks: SMS['tasks'];
};

export const SMSTask = ({ tasks }: SMSTaskProps) => {
  const { t } = useTranslation('thread-body');
  const alert = useAlert();
  const { openTaskWithTaskId } = useTaskCitation();

  const taskData = useMemo(() => getTasksData(tasks), [tasks]);

  const latestSMSTask = getLatestSMSTask(tasks);

  if (!taskData) {
    return null;
  }

  let chipTask = latestSMSTask;
  if (latestSMSTask?.status === TaskStatus.TASK_STATUS_DONE && (tasks?.length ?? 0) > 1) {
    chipTask =
      tasks?.find(
        (task) => task.status === TaskStatus.TASK_STATUS_TO_DO || task.status === TaskStatus.TASK_STATUS_IN_PROGRESS
      ) || latestSMSTask;
  }

  const onClickTask = (latestTaskId?: string) => {
    if (!latestTaskId) {
      alert.error(t('No tasks found'));
      return;
    }
    openTaskWithTaskId(latestTaskId);
  };

  const taskType = chipTask?.createdBy === AI_UUID ? 'auto' : 'manual';

  const taskTrackingId = `${InboxPrefixes.Thread}-task-chip-${taskType}`;

  return (
    <>
      {chipTask?.status === TaskStatus.TASK_STATUS_TO_DO && (
        <Chip.Task
          tasks={0}
          variant='task'
          onClick={() => onClickTask(chipTask.taskId)}
          type={taskType}
          trackingId={taskTrackingId}
        >
          {t('To Do')}
        </Chip.Task>
      )}
      {chipTask?.status === TaskStatus.TASK_STATUS_IN_PROGRESS && (
        <Chip.Task
          tasks={0}
          variant='task'
          onClick={() => onClickTask(chipTask.taskId)}
          type={taskType}
          trackingId={taskTrackingId}
        >
          {t('In Progress')}
        </Chip.Task>
      )}
      {chipTask?.status === TaskStatus.TASK_STATUS_DONE && (
        <Chip.Task
          tasks={0}
          variant='done'
          onClick={() => onClickTask(chipTask.taskId)}
          type={taskType}
          trackingId={taskTrackingId}
        >
          {t('Done')}
        </Chip.Task>
      )}
    </>
  );
};

SMSTask.displayName = Chat.Tag.displayName;
